// TelcoTrack — obnova scroll pozice při návratu ze detailu (tabulka → specifikace/dashboard).
// Skutečný scrollport je `<main.app-page-scroll>`, ne `window`.

import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

const STORAGE_PREFIX = 'tt.pageScroll.v1:';
const MAX_AGE_MS = 60 * 60 * 1000; // 1 h

export interface PageScrollSnapshot {
  scrollTop: number;
  scrollLeft: number;
  rowId?: string;
  savedAt: number;
}

@Injectable({ providedIn: 'root' })
export class PageScrollPositionService {
  private pendingRestoreKey: string | null = null;

  constructor(private readonly router: Router) {
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((e) => {
        // Before leaving, remember current scroll for the pathname we are on.
        this.captureCurrent();
        const targetPath = this.pathKeyFromUrl(e.url);
        if (this.read(targetPath)) {
          this.pendingRestoreKey = targetPath;
        }
      });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const key = this.pathKeyFromUrl(e.urlAfterRedirects);
        if (this.pendingRestoreKey && this.pendingRestoreKey === key) {
          // Defer until after Angular paints the destination view (and list tables mount).
          queueMicrotask(() => {
            requestAnimationFrame(() => this.restore(key));
          });
        }
        this.pendingRestoreKey = null;
      });
  }

  /** Explicitní zapamatování před navigací (řádek tabulky). */
  rememberForCurrentRoute(options?: { rowId?: string }): void {
    this.captureCurrent(options?.rowId);
  }

  /** Volitelné: jednorázově obnovit po asynchronním načtení tabulky. */
  restoreCurrentRoute(): void {
    this.restore(this.pathKeyFromUrl(this.router.url));
  }

  getScrollContainer(): HTMLElement | null {
    if (typeof document === 'undefined') return null;
    return document.querySelector<HTMLElement>('main.app-page-scroll');
  }

  private captureCurrent(rowId?: string): void {
    const el = this.getScrollContainer();
    if (!el) return;
    const key = this.pathKeyFromUrl(this.router.url);
    if (!key) return;
    const existing = this.read(key);
    const snapshot: PageScrollSnapshot = {
      scrollTop: el.scrollTop,
      scrollLeft: el.scrollLeft,
      rowId: rowId?.trim() || existing?.rowId,
      savedAt: Date.now(),
    };
    try {
      sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(snapshot));
    } catch {
      /* private mode / quota */
    }
  }

  private restore(key: string): void {
    const snapshot = this.read(key);
    if (!snapshot) return;
    const el = this.getScrollContainer();
    if (!el) return;

    el.scrollTop = snapshot.scrollTop;
    el.scrollLeft = snapshot.scrollLeft;

    const rowId = snapshot.rowId?.trim();
    if (rowId) {
      const row = document.querySelector<HTMLElement>(
        `[data-list-row-id="${this.cssEscape(rowId)}"]`,
      );
      if (!row) {
        // Table not rendered yet (async load) — keep snapshot for a later restoreCurrentRoute().
        return;
      }
      row.scrollIntoView({ block: 'center', inline: 'nearest' });
    }

    try {
      sessionStorage.removeItem(STORAGE_PREFIX + key);
    } catch {
      /* ignore */
    }
  }

  private cssEscape(value: string): string {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }
    return value.replace(/["\\]/g, '\\$&');
  }

  private read(key: string): PageScrollSnapshot | null {
    if (!key) return null;
    try {
      const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as PageScrollSnapshot;
      if (!parsed || typeof parsed.scrollTop !== 'number') return null;
      if (Date.now() - (parsed.savedAt || 0) > MAX_AGE_MS) {
        sessionStorage.removeItem(STORAGE_PREFIX + key);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  /** Key by pathname only (filters in query stay compatible with location.back()). */
  private pathKeyFromUrl(url: string): string {
    const path = (url ?? '').split('?')[0].split('#')[0].trim();
    if (!path) return '';
    return path.startsWith('/') ? path : `/${path}`;
  }
}
